/**
 * MUI ThemeProvider wrapper that creates a MUI theme from workspace presets,
 * sets CSS custom properties (--verisa-*), detects system color scheme,
 * and supports light/dark/system mode toggle.
 *
 * @module theme/ThemeProvider
 * @see TASK-FRONTEND-THEME-PROVIDER-CHG-001
 * @see SPEC-FRONTEND-RULE-007
 * @see SPEC-FRONTEND-RULE-008
 */
import React, { useEffect, useMemo } from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  CssBaseline,
  Theme,
} from "@mui/material";
import type { ThemeOptions } from "@mui/material/styles";
import { useLingui } from "@lingui/react";
import {
  lightThemeOptions,
  darkThemeOptions,
  themeToCssVars,
  applyWorkspacePreset,
  applyBrandPalette,
  type BrandPalette,
} from "./themes";
import {
  ThemeContextProvider,
  useVerisaTheme,
} from "./ThemeContext";
import type { CvdVariant } from "./cvdPalettes";
import { muiLocalizationFor } from "./muiLocale";

// ---------------------------------------------------------------------------
// Lazy import for CVD palettes (only loaded when needed)
// ---------------------------------------------------------------------------

let _cvdPalettes: Record<CvdVariant, (base: ThemeOptions) => ThemeOptions> | null = null;

async function getCvdPalettes(): Promise<
  Record<CvdVariant, (base: ThemeOptions) => ThemeOptions>
> {
  if (!_cvdPalettes) {
    const mod = await import("./cvdPalettes");
    _cvdPalettes = mod.cvdOverrides;
  }
  return _cvdPalettes;
}

// ---------------------------------------------------------------------------
// Inner provider (must be inside ThemeContextProvider)
// ---------------------------------------------------------------------------

interface InnerProps {
  children: React.ReactNode;
  /** Per-app component overrides — see VerisaThemeProviderProps.components. */
  components?: ThemeOptions["components"];
  /** The brand palette - the tenant's on a tenant's own host, else the installation's - see VerisaThemeProviderProps.brandPalette. */
  brandPalette?: BrandPalette;
}

function InnerThemeProvider({
  children,
  components,
  brandPalette,
}: InnerProps): React.ReactElement {
  const {
    effectiveMode,
    workspacePreset,
    cvdVariant,
    reducedMotion,
    fontSize,
    fontFamily,
  } = useVerisaTheme();
  // The active UI locale, for MUI's own component strings (./muiLocale). Every
  // SPA root mounts this provider inside I18nProvider (below BrandingProvider,
  // which needs it too), and I18nProvider re-renders on a locale switch.
  const { i18n } = useLingui();
  const locale = i18n.locale;

  // CVD overlay state: we import palettes synchronously after first load
  const [cvdOverrides, setCvdOverrides] = React.useState<
    Record<CvdVariant, (base: ThemeOptions) => ThemeOptions> | null
  >(null);

  useEffect(() => {
    if (cvdVariant && !cvdOverrides) {
      getCvdPalettes().then(setCvdOverrides).catch((err) => {
        // Graceful degradation: base theme still renders, but log so CVD regressions are diagnosable.
        console.warn("[ThemeProvider] Failed to load CVD palettes:", err);
      });
    }
  }, [cvdVariant, cvdOverrides]);

  // Build the final theme
  const theme: Theme = useMemo(() => {
    let options: ThemeOptions =
      effectiveMode === "dark" ? darkThemeOptions : lightThemeOptions;

    // Apply workspace preset
    if (workspacePreset) {
      options = applyWorkspacePreset(options, workspacePreset);
    }

    // Apply the brand palette - the tenant's on a tenant's own host, else the
    // installation's (SPEC-PLATFORM-BRANDING RULE-007).
    // AFTER the workspace preset on purpose: no preset may displace the brand on
    // the frame (RULE-007 as amended). The preset has NO production writer today -
    // the workspace chat page stopped bridging its deploy-channel colours into it
    // (D-SCA-14, D-SCA-15; SPEC-FRONTEND-RULE-008 as amended: "the preset
    // mechanism in web/src/theme stays, with no writer") - so the order guards
    // any future writer.
    // BEFORE the CVD overlay, which exists to rewrite whatever palette is in
    // force for a colour-vision deficiency and must therefore see the brand, and
    // before the per-app component overrides, which are not palette at all.
    // applyBrandPalette drops a malformed value and, on the dark options, a
    // channel failing SPEC-FRONTEND-RULE-084's contrast gate (D-BR-18).
    if (brandPalette) {
      options = applyBrandPalette(options, brandPalette);
    }

    // Apply CVD palette overrides
    if (cvdVariant && cvdOverrides?.[cvdVariant]) {
      options = cvdOverrides[cvdVariant](options);
    }

    // Per-app component overrides (CHG-003). SHALLOW, per-component REPLACE --
    // deliberately the same semantics an app gets today from
    // `createTheme({...lightThemeOptions, components: {...}})`, because Manage's
    // MuiButton/MuiPaper REPLACE the shared entries rather than extending them.
    // A deep merge here would hand Manage's buttons and papers the shared
    // `borderRadius: 8` they do not have today -- a silent restyle of a whole SPA
    // smuggled in under a dark-mode change.
    if (components) {
      options = {
        ...options,
        components: { ...(options.components ?? {}), ...components },
      };
    }

    // Override font size & family from user settings
    options = {
      ...options,
      typography: {
        ...(options.typography as Record<string, unknown>),
        fontSize,
        fontFamily,
      },
    };

    // Reduced-motion component overrides
    if (reducedMotion) {
      options = {
        ...options,
        transitions: {
          create: () => "none",
        },
        components: {
          ...(options.components ?? {}),
          MuiButtonBase: {
            defaultProps: {
              disableRipple: true,
            },
          },
        },
      };
    }

    // MUI's default strings (Autocomplete "No options", TablePagination "Rows
    // per page:", the close/clear labels) follow the UI locale, not English.
    return createTheme(options, muiLocalizationFor(locale));
  }, [
    locale,
    effectiveMode,
    workspacePreset,
    brandPalette,
    cvdVariant,
    cvdOverrides,
    reducedMotion,
    fontSize,
    fontFamily,
    components,
  ]);

  // Inject CSS custom properties into :root
  useEffect(() => {
    const baseOptions: ThemeOptions =
      effectiveMode === "dark" ? darkThemeOptions : lightThemeOptions;

    let options = workspacePreset
      ? applyWorkspacePreset(baseOptions, workspacePreset)
      : baseOptions;

    // The same brand application, in the same order, so --verisa-primary and
    // theme.palette.primary.main cannot disagree - a plugin styling itself from
    // the custom properties and a component reading the palette are then the same
    // colour (SPEC-PLATFORM-BRANDING RULE-007). That holds only with no CVD
    // variant active: the memo above applies the CVD overlay after the brand and
    // this effect does not, so under a CVD variant the palette is the rewritten
    // one and the custom properties are not.
    if (brandPalette) {
      options = applyBrandPalette(options, brandPalette);
    }

    // Apply font overrides to the options used for CSS vars
    options = {
      ...options,
      typography: {
        ...(options.typography as Record<string, unknown>),
        fontSize,
        fontFamily,
      },
    };

    const vars = themeToCssVars(options);
    const root = document.documentElement;

    for (const [prop, value] of Object.entries(vars)) {
      if (value) {
        root.style.setProperty(prop, value);
      }
    }

    return () => {
      for (const prop of Object.keys(vars)) {
        root.style.removeProperty(prop);
      }
    };
  }, [effectiveMode, workspacePreset, brandPalette, fontSize, fontFamily]);

  // Set data-theme attribute for CSS selectors
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", effectiveMode);
    return () => {
      document.documentElement.removeAttribute("data-theme");
    };
  }, [effectiveMode]);

  // Reduced motion class
  useEffect(() => {
    if (reducedMotion) {
      document.documentElement.classList.add("verisa-reduced-motion");
    } else {
      document.documentElement.classList.remove("verisa-reduced-motion");
    }
    return () => {
      document.documentElement.classList.remove("verisa-reduced-motion");
    };
  }, [reducedMotion]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </MuiThemeProvider>
  );
}

// ---------------------------------------------------------------------------
// Public ThemeProvider (wraps context + MUI provider)
// ---------------------------------------------------------------------------

export interface VerisaThemeProviderProps {
  children: React.ReactNode;
  defaultMode?: "light" | "dark" | "system";
  /**
   * Per-app MUI component overrides, merged over the shared `baseComponents`
   * with per-component REPLACE semantics (not a deep merge) -- see the note at
   * the merge site. This exists so an SPA carrying its own overrides can adopt
   * the mode-aware provider without its overrides being silently widened.
   */
  components?: ThemeOptions["components"];
  /**
   * The brand palette - the tenant's on a tenant's own host, else the
   * installation's - as LITERAL `#rrggbb` values and never a CSS
   * custom property (SPEC-FRONTEND-RULE-081 as amended by
   * SPEC-PLATFORM-BRANDING-RULE-007, which names this prop as the third
   * palette-hex source beside palettes.ts and cvdPalettes.ts).
   *
   * Each SPA root reads the value from the BrandingProvider mounted ABOVE this
   * provider (UI-004) and passes it here; this provider never fetches it. A
   * malformed channel is dropped rather than applied, and on the dark theme a
   * channel that fails the RULE-084 contrast gate keeps its base value (D-BR-18)
   * - see applyBrandPalette in ./themes.
   */
  brandPalette?: BrandPalette;
}

/**
 * Verisa ThemeProvider — wraps children with theme context and MUI
 * ThemeProvider. Sets CSS custom properties (--verisa-*) on :root.
 *
 * Usage:
 * ```tsx
 * <VerisaThemeProvider>
 *   <App />
 * </VerisaThemeProvider>
 * ```
 */
export function VerisaThemeProvider({
  children,
  defaultMode,
  components,
  brandPalette,
}: VerisaThemeProviderProps): React.ReactElement {
  return (
    <ThemeContextProvider defaultMode={defaultMode}>
      <InnerThemeProvider components={components} brandPalette={brandPalette}>
        {children}
      </InnerThemeProvider>
    </ThemeContextProvider>
  );
}

export default VerisaThemeProvider;
